import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api.js';
import { Package, Plus, Edit2, Trash2, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import {
  ManufacturerGroup,
  type GroupProduct,
  type ManufacturerGroupData,
} from '../components/products/ManufacturerGroup';
import {
  EMPTY_MARKETING,
  ProductMarketingFields,
  marketingFormFrom,
  marketingPayload,
  type ProductMarketingForm,
} from '../components/products/ProductMarketingFields';

interface Manufacturer {
  id: string;
  name: string;
}

type Product = GroupProduct;

interface ProductFormState extends ProductMarketingForm {
  name: string;
  manufacturer_id: string;
  campaign_name: string;
  client_company: string;
}

const EMPTY_FORM: ProductFormState = {
  name: '',
  manufacturer_id: '',
  campaign_name: '',
  client_company: '',
  ...EMPTY_MARKETING,
};

function buildGroups(
  manufacturers: Manufacturer[],
  products: Product[],
  filterManufacturerId: string,
): ManufacturerGroupData[] {
  const map = new Map<string, ManufacturerGroupData>();

  for (const m of manufacturers) {
    if (filterManufacturerId && m.id !== filterManufacturerId) continue;
    map.set(m.id, {
      manufacturerId: m.id,
      manufacturerName: m.name,
      products: [],
    });
  }

  for (const p of products) {
    if (filterManufacturerId && p.manufacturer_id !== filterManufacturerId) continue;
    const existing = map.get(p.manufacturer_id);
    if (existing) {
      existing.products.push(p);
    } else {
      map.set(p.manufacturer_id, {
        manufacturerId: p.manufacturer_id,
        manufacturerName: p.manufacturer_name,
        products: [p],
      });
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    a.manufacturerName.localeCompare(b.manufacturerName),
  );
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [manufacturersError, setManufacturersError] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('active');
  const [filterManufacturerId, setFilterManufacturerId] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProductFormState>(EMPTY_FORM);

  useEffect(() => {
    void fetchManufacturers();
  }, []);

  useEffect(() => {
    void fetchProducts();
  }, [filterStatus, filterManufacturerId]);

  const fetchManufacturers = async (): Promise<Manufacturer[]> => {
    try {
      const res = await api.get('/manufacturers');
      const list = res.data as Manufacturer[];
      setManufacturers(list);
      setManufacturersError(false);
      return list;
    } catch {
      setManufacturersError(true);
      toast.error('Failed to load manufacturers');
      return [];
    }
  };

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus === 'active') params.set('is_active', 'true');
      else if (filterStatus === 'inactive') params.set('is_active', 'false');
      if (filterManufacturerId) params.set('manufacturer_id', filterManufacturerId);
      const res = await api.get(`/products?${params.toString()}`);
      setProducts(res.data);
    } catch {
      console.error('Failed to fetch products');
    }
  };

  const grouped = useMemo(
    () => buildGroups(manufacturers, products, filterManufacturerId),
    [manufacturers, products, filterManufacturerId],
  );

  const resolveManufacturerId = (
    list: Manufacturer[],
    preferred?: string,
    current?: string,
  ): string => {
    if (preferred && list.some((m) => m.id === preferred)) return preferred;
    if (current && list.some((m) => m.id === current)) return current;
    return list[0]?.id || '';
  };

  const handleOpenForm = async (product?: Product, manufacturerId?: string) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        manufacturer_id: product.manufacturer_id,
        campaign_name: product.campaign_name || '',
        client_company: product.client_company || '',
        ...marketingFormFrom(product),
      });
    } else {
      setEditingProduct(null);
      setFormData({
        ...EMPTY_FORM,
        manufacturer_id: manufacturerId || manufacturers[0]?.id || '',
      });
    }
    setIsFormOpen(true);
    const list = await fetchManufacturers();
    if (!product) {
      setFormData((prev) => ({
        ...prev,
        manufacturer_id: resolveManufacturerId(list, manufacturerId, prev.manufacturer_id),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.manufacturer_id) return;
    setSaving(true);
    const payload = {
      name: formData.name,
      manufacturer_id: formData.manufacturer_id,
      campaign_name: formData.campaign_name,
      client_company: formData.client_company,
      ...marketingPayload(formData),
    };
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product added');
      }
      setIsFormOpen(false);
      void fetchProducts();
    } catch {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await api.patch(`/products/${id}/toggle-active`);
      toast.success(`Product ${currentStatus ? 'deactivated' : 'activated'}`);
      void fetchProducts();
    } catch {
      toast.error('Failed to toggle product status');
    }
  };

  const handleDeletePermanent = async () => {
    if (!productToDelete) return;
    try {
      await api.delete(`/products/${productToDelete}/permanent`);
      toast.success('Product deleted permanently');
      void fetchProducts();
    } catch {
      toast.error('Cannot delete product referenced in visits/orders');
    }
  };

  const toggleExpand = (productId: string) => {
    setExpandedProductId((prev) => (prev === productId ? null : productId));
  };

  const renderActions = (p: Product) => (
    <div className="flex justify-end gap-2">
      <button
        onClick={() => void handleOpenForm(p)}
        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-brand-600 transition-colors"
        title="Edit"
      >
        <Edit2 size={16} />
      </button>
      <button
        onClick={() => void handleToggleActive(p.id, p.is_active)}
        className={`p-2 rounded-lg transition-colors ${
          p.is_active
            ? 'hover:bg-orange-50 text-gray-400 hover:text-orange-600'
            : 'hover:bg-green-50 text-gray-400 hover:text-green-600'
        }`}
        title={p.is_active ? 'Disable' : 'Enable'}
      >
        {p.is_active ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
      </button>
      <button
        onClick={() => {
          setProductToDelete(p.id);
          setDeleteDialogOpen(true);
        }}
        className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors"
        title="Delete"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );

  const noManufacturers = manufacturers.length === 0;
  const canSubmit = !saving && !noManufacturers && !!formData.manufacturer_id;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-3">
        <div>
          <h1 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight">
            Campaign Products
          </h1>
          <p className="text-gray-500 font-medium font-mono uppercase text-xs tracking-tighter">
            Master Catalog Management
          </p>
        </div>
        <button
          onClick={() => void handleOpenForm()}
          className="flex items-center justify-center gap-2 bg-brand-600 text-white px-5 py-3
            rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-brand-700
            transition-all active:scale-95 shadow-lg shadow-brand-100"
        >
          <Plus size={18} />
          Add Product
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {(['active', 'inactive', 'all'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-4 py-2.5 rounded-xl font-bold uppercase text-xs tracking-wider transition-all ${
              filterStatus === s
                ? s === 'active'
                  ? 'bg-green-600 text-white'
                  : s === 'inactive'
                    ? 'bg-orange-600 text-white'
                    : 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s}
          </button>
        ))}
        <div className="relative">
          <select
            value={filterManufacturerId}
            onChange={(e) => setFilterManufacturerId(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 rounded-xl font-bold text-xs
              bg-gray-100 text-gray-600 border-0 focus:ring-2 focus:ring-brand-500 cursor-pointer"
          >
            <option value="">All Manufacturers</option>
            {manufacturers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>
        {manufacturersError && (
          <button
            type="button"
            onClick={() => void fetchManufacturers()}
            className="px-3 py-2.5 rounded-xl font-bold uppercase text-xs tracking-wider
              bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
          >
            Retry manufacturers
          </button>
        )}
      </div>

      <div className="hidden md:block bg-white rounded-[2.5rem] border-2 border-gray-100 shadow-sm overflow-hidden">
        {grouped.length === 0 ? (
          <div className="py-16 text-center text-gray-400 font-bold uppercase text-xs italic">
            No products found.
          </div>
        ) : (
          grouped.map((group) => (
            <ManufacturerGroup
              key={group.manufacturerId}
              group={group}
              layout="desktop"
              expandedProductId={expandedProductId}
              onToggleExpand={toggleExpand}
              onAddProduct={(id) => void handleOpenForm(undefined, id)}
              renderActions={renderActions}
              onVariantsChanged={() => void fetchProducts()}
            />
          ))
        )}
      </div>

      <div className="md:hidden space-y-4">
        {grouped.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-gray-300">
            <Package size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-bold text-gray-600">No products found</p>
          </div>
        ) : (
          grouped.map((group) => (
            <ManufacturerGroup
              key={group.manufacturerId}
              group={group}
              layout="mobile"
              expandedProductId={expandedProductId}
              onToggleExpand={toggleExpand}
              onAddProduct={(id) => void handleOpenForm(undefined, id)}
              renderActions={renderActions}
              onVariantsChanged={() => void fetchProducts()}
            />
          ))
        )}
      </div>

      {isFormOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end
            md:items-center justify-center p-0 md:p-4"
        >
          <div
            className="bg-white w-full md:max-w-lg rounded-t-[2rem] md:rounded-[2.5rem]
              shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div className="p-5 md:p-8 overflow-y-auto flex-1">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-black text-gray-900">
                  {editingProduct ? 'Update' : 'Add'} Product
                </h2>
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="text-gray-400 hover:text-gray-600 font-black text-sm"
                >
                  CLOSE
                </button>
              </div>
              <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    Manufacturer
                  </label>
                  <select
                    required
                    value={formData.manufacturer_id}
                    onChange={(e) =>
                      setFormData({ ...formData, manufacturer_id: e.target.value })
                    }
                    disabled={noManufacturers}
                    className="w-full p-3 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold
                      focus:border-brand-500 transition-colors text-base disabled:opacity-50"
                  >
                    <option value="">-- Select Manufacturer --</option>
                    {manufacturers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  {noManufacturers && (
                    <p className="mt-2 text-xs font-bold text-orange-600">
                      Create a manufacturer first before adding products.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    Product Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Yerba Mate Classic"
                    className="w-full p-3 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold
                      focus:border-brand-500 transition-colors text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    Campaign Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.campaign_name}
                    onChange={(e) =>
                      setFormData({ ...formData, campaign_name: e.target.value })
                    }
                    placeholder="e.g. Berlin Winter 2025"
                    className="w-full p-3 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold
                      focus:border-brand-500 transition-colors text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    Client Company
                  </label>
                  <input
                    type="text"
                    value={formData.client_company}
                    onChange={(e) =>
                      setFormData({ ...formData, client_company: e.target.value })
                    }
                    placeholder="e.g. Nightwatch Beverages GmbH"
                    className="w-full p-3 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold
                      focus:border-brand-500 transition-colors text-base"
                  />
                </div>
                <ProductMarketingFields
                  value={formData}
                  onChange={(marketing) =>
                    setFormData((prev) => ({ ...prev, ...marketing }))
                  }
                />
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full py-4 bg-brand-600 text-white rounded-2xl font-black uppercase
                    tracking-widest hover:bg-brand-700 transition-all active:scale-95
                    disabled:opacity-50 text-sm"
                >
                  {saving ? 'Saving…' : editingProduct ? 'Update Product' : 'Add to Catalog'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Product?"
        message="This will permanently delete this product. This cannot be undone."
        confirmText="Delete Product"
        variant="danger"
        requireTypedConfirmation={true}
        confirmationKeyword="DELETE"
        onConfirm={handleDeletePermanent}
      />
    </div>
  );
};

export default Products;
